
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ChevronLeft, 
  Download, 
  Mail, 
  Printer, 
  Loader2,
  Building2,
  User,
  Calendar,
  DollarSign,
  PiggyBank,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { usePayslip, useDownloadPayslip, useEmailPayslip } from '@/hooks/usePayRuns';
import { formatCurrency, formatDate } from '@/lib/utils';

export function PayslipViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: payslip, isLoading } = usePayslip(id!);
  const downloadPayslip = useDownloadPayslip();
  const emailPayslip = useEmailPayslip();

  const handleDownload = async () => {
    try {
      const blob = await downloadPayslip.mutateAsync(id!);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payslip-${payslip?.payRunNumber}-${payslip?.item.employeeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: 'Success',
        description: 'Payslip downloaded successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to download payslip',
        variant: 'destructive',
      });
    }
  };

  const handleEmail = async () => {
    try {
      await emailPayslip.mutateAsync(id!);
      toast({
        title: 'Success',
        description: 'Payslip emailed successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to email payslip',
        variant: 'destructive',
      });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!payslip) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Payslip not found</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Payslip</h1>
            <p className="text-sm text-muted-foreground">
              {payslip.payRunNumber} • {formatDate(payslip.periodStartDate)} - {formatDate(payslip.periodEndDate)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="mr-2 h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" onClick={handleEmail} disabled={emailPayslip.isPending}>
            {emailPayslip.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Mail className="mr-2 h-4 w-4" />
            Email
          </Button>
          <Button onClick={handleDownload} disabled={downloadPayslip.isPending}>
            {downloadPayslip.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
        </div>
      </div>

      {/* Payslip Preview */}
      <Card className="max-w-4xl mx-auto print:shadow-none print:border-none">
        <CardContent className="p-8 print:p-0">
          {/* Payslip Header */}
          <div className="flex justify-between items-start border-b pb-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 bg-primary/10 rounded-lg flex items-center justify-center print:bg-gray-100">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-bold">AxiomVanta Pty Ltd</h2>
                <p className="text-sm text-muted-foreground">ABN: 12 123 123 123</p>
                <p className="text-sm text-muted-foreground">Sydney, NSW 2000</p>
              </div>
            </div>
            <div className="text-right">
              <h3 className="text-2xl font-bold text-primary">PAYSLIP</h3>
              <p className="text-lg font-medium">{payslip.payRunNumber}</p>
              <Badge variant="outline" className="mt-2">
                {formatDate(payslip.paymentDate)}
              </Badge>
            </div>
          </div>

          {/* Employee Details */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Employee</p>
                <p className="font-medium text-lg">{payslip.item.employeeName}</p>
                <p className="text-sm">{payslip.item.employeeCode}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">Pay Period</p>
                <p className="font-medium">
                  {formatDate(payslip.periodStartDate)} - {formatDate(payslip.periodEndDate)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Payment Date: {formatDate(payslip.paymentDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Pay Details */}
          <div className="space-y-6">
            {/* Earnings */}
            <div>
              <h4 className="font-semibold text-muted-foreground uppercase text-sm mb-3">Earnings</h4>
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-2 text-sm font-medium">Description</th>
                    <th className="text-right p-2 text-sm font-medium">Hours</th>
                    <th className="text-right p-2 text-sm font-medium">Rate</th>
                    <th className="text-right p-2 text-sm font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-2">Ordinary Hours</td>
                    <td className="p-2 text-right">{payslip.item.ordinaryHours.toFixed(2)}</td>
                    <td className="p-2 text-right">{formatCurrency(payslip.item.ordinaryRate)}</td>
                    <td className="p-2 text-right font-medium">{formatCurrency(payslip.item.ordinaryAmount)}</td>
                  </tr>
                  {payslip.item.overtimeHours > 0 && (
                    <tr className="border-b">
                      <td className="p-2">Overtime</td>
                      <td className="p-2 text-right">{payslip.item.overtimeHours.toFixed(2)}</td>
                      <td className="p-2 text-right">{formatCurrency(payslip.item.overtimeRate)}</td>
                      <td className="p-2 text-right font-medium">{formatCurrency(payslip.item.overtimeAmount)}</td>
                    </tr>
                  )}
                  {payslip.item.allowances.map((allowance) => (
                    <tr key={allowance.id} className="border-b">
                      <td className="p-2">{allowance.description || allowance.type}</td>
                      <td className="p-2 text-right">-</td>
                      <td className="p-2 text-right">-</td>
                      <td className="p-2 text-right font-medium">{formatCurrency(allowance.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between p-2 bg-muted/50 font-semibold">
                <span>Gross Pay</span>
                <span>{formatCurrency(payslip.item.gross)}</span>
              </div>
            </div>

            {/* Deductions */}
            {(payslip.item.deductions.length > 0 || payslip.item.tax > 0) && (
              <div>
                <h4 className="font-semibold text-muted-foreground uppercase text-sm mb-3">Deductions</h4>
                <table className="w-full">
                  <tbody>
                    <tr className="border-b">
                      <td className="p-2">PAYG Tax</td>
                      <td className="p-2 text-right font-medium">{formatCurrency(payslip.item.tax)}</td>
                    </tr>
                    {payslip.item.deductions.map((deduction) => (
                      <tr key={deduction.id} className="border-b">
                        <td className="p-2">{deduction.description || deduction.type}</td>
                        <td className="p-2 text-right font-medium">{formatCurrency(deduction.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Net Pay */}
            <div className="bg-primary/5 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <span className="text-lg font-bold">Net Pay</span>
                </div>
                <span className="text-2xl font-bold text-primary">{formatCurrency(payslip.item.netPay)}</span>
              </div>
            </div>

            {/* Superannuation */}
            <div className="flex items-start gap-3 p-4 border rounded-lg">
              <PiggyBank className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">Superannuation Contribution</p>
                <p className="text-2xl font-bold">{formatCurrency(payslip.item.superContribution)}</p>
                <p className="text-sm text-muted-foreground">
                  Paid to your nominated super fund
                </p>
              </div>
            </div>

            {/* YTD Summary */}
            <div>
              <h4 className="font-semibold text-muted-foreground uppercase text-sm mb-3">Year to Date</h4>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Gross</p>
                  <p className="font-bold">{formatCurrency(payslip.ytdTotals.gross)}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Tax</p>
                  <p className="font-bold">{formatCurrency(payslip.ytdTotals.tax)}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Super</p>
                  <p className="font-bold">{formatCurrency(payslip.ytdTotals.super)}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Net</p>
                  <p className="font-bold">{formatCurrency(payslip.ytdTotals.net)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t text-center text-sm text-muted-foreground">
            <p>This is an official payslip from AxiomVanta Pty Ltd</p>
            <p>Generated on {formatDate(payslip.generatedAt)}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
